import React from 'react';
import { useRouter } from '@tanstack/react-router';
import { Card, Space, Tabs, Typography, Spin, Alert, Tag, Button, Table } from 'antd';
import type { TabsProps } from 'antd';
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { get, post } from '@/common/services/httpClient';
import type { ApiResponse } from '../../../auth/types/authTypes';
import DynamicTable from '../../../../common/components/DynamicTable/DynamicTable';
import type { ColumnsType } from 'antd/es/table';
// import type { UserType } from '../../../management/types/managementTypes';
import type { MedicalProviderType, ServiceItemPricingType } from '../../types/providersTypes';
import type { ClaimRequestType } from '../../../claim/types/claimType';
import { API_ENDPOINTS as API_MANAGEMENT_ENDPOINTS, QUERY_KEYS as QUERY_MANAGEMENT_KEYS } from '../../../management/api/apiEndpoints';

import dayjs from 'dayjs';
import { CLAIM_STATUS } from '@/common/staticData/options';
import StatusTag from '../../../../common/components/Status';
import { ClaimStatusName, ClaimStatus } from '../../../../common/staticData/enums';
import type { UserType } from '../../../management/types/managementTypes';
import { t } from 'i18next';
import {
    CheckCircleOutlined,
    StopOutlined,
    SyncOutlined,
    PlusOutlined,
    EditOutlined,
} from "@ant-design/icons"
import Confirmation from '../../../../common/components/ConfirmButton';

const { Title, Text } = Typography;

// Fetch function
const fetchDetail = async (Id: string): Promise<MedicalProviderType> => {
    const response = await get<ApiResponse<MedicalProviderType>>(
        `${API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_BY_ID}/${Id}`,
    );
    return response.data;
};

// Pricing Columns
const pricingColumns = (): ColumnsType<ServiceItemPricingType> => [
    {
        title: t('Main Service'),
        dataIndex: 'serviceCategoryName',
        key: 'serviceCategoryName',
        width: 250,
    },
    {
        title: t('Sub Service'),
        dataIndex: 'serviceCategoryItemName',
        key: 'serviceCategoryItemName',
        width: 200,
    },
    {
        title: t('Price'),
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
    },
    {
        title: t('Active'),
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        render: (isActive: boolean) => (
            <Tag color={isActive ? 'green' : 'red'}>
                {isActive ? 'Yes' : 'No'}
            </Tag>
        ),
    },
];

const fetchMedicalProviderUsers = async (medicalProviderId: string): Promise<UserType[]> => {
    const payload = { medicalProviderId };
    const response = await post<ApiResponse<UserType[]>>(
        API_ENDPOINTS.PROVIDERS.GET_USERS,
        payload,
    );
    return response.data;
};

const fetchMedicalProviderPricing = async (medicalProviderId: string): Promise<ServiceItemPricingType[]> => {
    const payload = { medicalProviderId };
    const response = await post<ApiResponse<ServiceItemPricingType[]>>(
        API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_SERVICESITEMS_PRICING,
        payload,
    );
    return response.data;
};



// --- Main Component ---
const Detail: React.FC = () => {
    const router = useRouter();
    const state = router?.state?.location?.state as { record?: MedicalProviderType } | undefined;
    const record = state?.record;

    const Id = record?.id?.toString() || '';

    const { data: medicalProviderData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.PROVIDERS.MEDICALPROVIDERS, Id],
        () => fetchDetail(Id),
        { enabled: !!Id },
    );

    // ============== Fetch Users for User Tab ==============
    const {
        data: userData,
        isLoading: isUsersLoading,
        isError: isUsersError,
    } = useAppQuery(
        [QUERY_KEYS.PROVIDERS.GET_MEDICAL_PROVIDERS_DETAIL, Id],
        () => fetchMedicalProviderUsers(Id),
        { enabled: !!Id },
        );


    const {
        data: pricingData,
        isLoading: isPricingLoading,
        isError: isPricingError,
    } = useAppQuery(
        [QUERY_KEYS.PROVIDERS.MEDICALPROVIDER_SERVICE_ITEM_PRICING, Id],
        () => fetchMedicalProviderPricing(Id),
        { enabled: !!Id },
    );
    const onChange = (key: string) => console.log('Selected Tab:', key);

    // --- ClaimRequests logic inlined ---
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 15);

    // ============== User Columns ==============
    const userColumns = (router: ReturnType<typeof useRouter>): ColumnsType<UserType> => [
        { title: t('Name'), dataIndex: 'name', key: 'name', width: 150 },
        { title: t('User Name'), dataIndex: 'userName', key: 'userName', width: 150 },
        { title: t('Phone No'), dataIndex: 'cellNo', key: 'cellNo', width: 150 },
        {
            title: t('Active'),
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Yes' : 'No'}
                </Tag>
            ),
        },
        {
            title: t("Actions"),
            key: "actions",
            width: 20,
            render: (_: any, record: UserType) => (
                <>
                    {!record.isActive && !record.isEmailActivated && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.RESEND_ACTIVATION_EMAIL_USER}
                            id={record.email}
                            title="Resend Email"
                            method="post"
                            icon={<SyncOutlined />}
                            payloadKey="email"
                            confirmButtonText={t(`Are you sure you want to resend activation email ?`)}
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="Email sent successfully"
                        />
                    )}
                    {record.isEmailActivated && record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Deactivate"
                            method="post"
                            icon={<StopOutlined style={{ color: "red" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to deactivate this user?"
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="User deactivated successfully"
                        />
                    )}
                    {record.isEmailActivated && !record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Activate"
                            method="post"
                            icon={<CheckCircleOutlined style={{ color: "green" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to activate this user?"
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="User activated successfully"
                        />
                    )}
                    <Button
                        type="default"
                        icon={<EditOutlined style={{ color: '#1890ff' }} />}
                        title="Edit User"
                        style={{ marginLeft: 8, borderColor: '#1890ff' }}
                        onClick={async () => {
                            try {
                                const response = await get<ApiResponse<UserType>>(
                                    `${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}/${record?.id}`
                                );
                                const userDetail = response.data;
                                router.navigate({
                                    to: '/medical-provider/detail/user',
                                    state: {
                                        medicalProviderId: record?.medicalProviderId,
                                        record: userDetail,
                                        medicalProviderName: medicalProviderData?.name,
                                    } as Record<string, unknown>,
                                });
                            } catch (error) {
                                console.error("Error fetching user detail:", error);
                            }
                        }}
                    />
                </>
            ),
        },
    ];

    const claimColumns: ColumnsType<ClaimRequestType> = [
        { title: 'Reference No', dataIndex: 'referenceNo', key: 'referenceNo', fixed: 'left' },
        {
            title: 'Claim Date',
            dataIndex: 'claimDate',
            key: 'claimDate',
            render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        { title: 'Policy Code', dataIndex: 'policyCode', key: 'policyCode' },
        { title: 'Plan Name', dataIndex: 'planName', key: 'planName' },
        { title: 'Policy Holder', dataIndex: 'policyHolderName', key: 'policyHolderName' },
        { title: 'Patient Name', dataIndex: 'patientName', key: 'patientName' },
        { title: 'Medical Provider', dataIndex: 'medicalProviderName', key: 'medicalProviderName' },
        { title: 'Consultant', dataIndex: 'consultantName', key: 'consultantName' },
        {
            title: 'Visit Date',
            dataIndex: 'visitDate',
            key: 'visitDate',
            render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        //{ title: 'Consultant Fee', dataIndex: 'consultantFee', key: 'consultantFee' },
        //{ title: 'Medicine Amount', dataIndex: 'medicineAmount', key: 'medicineAmount' },
        //{ title: 'Lab Test Amount', dataIndex: 'labTestAmount', key: 'labTestAmount' },
        { title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount' },
        { title: 'Currency', dataIndex: 'currencyISOCode', key: 'currencyISOCode' },
        // { title: 'Status', dataIndex: 'status', key: 'status', fixed: 'right' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            fixed: 'right',
            render: (status: string) => (
                <StatusTag status={ClaimStatusName[ClaimStatus[status as keyof typeof ClaimStatus]]} />
            ),
        },
        // { title: 'Comment', dataIndex: 'comment', key: 'comment' },
    ];

    const Section = ({
        title,
        children,
    }: {
        title: string;
        children: React.ReactNode;
    }) => (
        <div
            style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: '24px',
                marginBottom: '24px',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
        >
            <h3 style={{ marginBottom: 16 }}>{title}</h3>
            {children}
        </div>
    );

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: t('Claim'),
            children: (
                <div>
                    <DynamicTable<ClaimRequestType>
                        dataApiUrl={API_ENDPOINTS.PROVIDERS.CLAIM_SEARCH}
                        baseQueryKey={QUERY_KEYS.PROVIDERS.MEDICALPROVIDERS}
                        columns={claimColumns}
                        tableTitle={'Claim Requests'}
                        showActions={false}
                        deleteBaseUrl={API_ENDPOINTS.PROVIDERS.CLAIM_DELETE}
                        extraParameters={{ medicalProviderId: Id }}
                        filterConfigs={[
                            {
                                field: 'companyId',
                                type: 'select',
                                placeholder: 'Select company',
                                showSearch: true,
                                dynamicOptions: {
                                    url: '/Lookup/GetDropdownData',
                                    method: 'POST',
                                    body: { dropdowns: ['company'] },
                                    transformData: (apiResponse: unknown) => {
                                        const response = apiResponse as { company: { Name: string; Id: number }[] };
                                        return response.company.map((item) => ({ label: item.Name, value: item.Id }));
                                    },
                                },
                            },
                            { field: 'ReferenceOrCardNo', placeholder: 'Search by reference or card number', type: 'input' },
                            {
                                field: 'fromDate',
                                placeholder: 'From Date',
                                type: 'date',
                                displayFormat: 'DD-MMM-YYYY',
                                defaultValue: dayjs(fromDate).format('YYYY-MM-DD'),
                            },
                            {
                                field: 'toDate',
                                placeholder: 'To Date',
                                type: 'date',
                                displayFormat: 'DD-MM-YYYY',
                                defaultValue: dayjs(toDate).format('YYYY-MM-DD'),
                            },
                            {
                                field: 'status',
                                placeholder: 'Status',
                                type: 'select',
                                options: CLAIM_STATUS,
                                defaultValue: null,
                            },
                        ]}
                    />
                </div>
            ),
            forceRender: true,
        },
        {
            key: '2',
            label: t('Pricing'),
            children: (
                <div>
                    <DynamicTable<ServiceItemPricingType>
                        dataApiUrl={API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_SERVICESITEMS_PRICING}
                        baseQueryKey={QUERY_KEYS.PROVIDERS.MEDICALPROVIDER_SERVICE_ITEM_PRICING}
                        columns={pricingColumns()}
                        tableTitle={t("Medical Provider Pricing")}
                        showActions={false}
                        showAddNew={false}
                        extraParameters={{ medicalProviderId: Id }}
                        filterConfigs={[
                            {
                                field: 'serviceItemCode',
                                placeholder: t('Service Item Code'),
                                type: 'input',
                            },
                            {
                                field: 'medicalProviderServiceCategoryName',
                                placeholder: t('Service Category'),
                                type: 'input',
                            },
                            {
                                field: 'medicalProviderServiceCategoryItemName',
                                placeholder: t('Service Item'),
                                type: 'input',
                            },
                        ]}
                        pagination={{ pageSize: 10 }}
                    />
                </div>
            ),
            forceRender: true,
        },
        {
            key: '3',
            label: t('User'),
            children: (
                <Section title="">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ margin: 0 }}>{t("User Management")}</h3>
                        <Button
                            type="primary"
                            onClick={() =>
                                router.navigate({
                                    to: '/medical-provider/detail/user',
                                    state: { medicalProviderId: record?.id, name: record?.name, medicalProviderName: record?.name } as Record<string, unknown>,
                                })
                            }
                        >
                            <PlusOutlined /> {t("Add User")}
                        </Button>
                    </div>

                    {isUsersLoading && <Spin size="large" />}
                    {isUsersError && <Alert type="error" message="Error fetching users" />}
                    {!isUsersLoading && !isUsersError && (
                        <Table<UserType>
                            columns={userColumns(router)}
                            dataSource={userData}
                            rowKey="id"
                            locale={{
                                emptyText: t("No data")
                            }}
                        />
                    )}
                </Section>
            ),
            forceRender: true,
        },
    ];

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <h3 style={{ margin: 0 }}>{medicalProviderData?.name || t('Medical Provider Detail')}</h3>

                    </Title>
                    <Space size="middle" wrap>
                        <Text>
                            {medicalProviderData?.address} {medicalProviderData?.country}
                        </Text>
                    </Space>
                    <Space size="middle" wrap>
                        <Text>{medicalProviderData?.email},</Text>
                    </Space>
                </div>
            </Card>

            {isLoading && <Spin size="large" />}
            {isError && <Alert type="error" message="Error fetching data" />}

            {!isLoading && !isError && (
                <Tabs
                    defaultActiveKey="1"
                    items={items}
                    onChange={onChange}
                    destroyInactiveTabPane={false}
                />
            )}
        </div>
    );
};

export default Detail;
